/* =====================================================================
 * ConnectionHealth — WebSocket 연결 상태 중앙 모니터링
 *
 * DM / Collab WebSocket의 상태를 통합 추적하고,
 * 연결 품질 지표를 제공합니다.
 *
 * 3단계 연결 상태: connected / reconnecting / disconnected
 * ===================================================================== */

export type ChannelName = 'dm' | 'collab';
export type HealthStatus = 'healthy' | 'degraded' | 'disconnected';
export type ConnectionPhase = 'connected' | 'reconnecting' | 'disconnected';

interface ChannelState {
  status: HealthStatus;
  phase: ConnectionPhase;
  lastPongAt: number;
  lastConnectedAt: number;
  reconnectCount: number;
  latencyMs: number;
  consecutiveFailures: number;
  lastReconnectAt: number;
  messagesReceived: number;
  messagesSent: number;
}

type HealthListener = (channel: ChannelName, state: ChannelState) => void;

const HEALTHY_THRESHOLD_MS = 45_000;
const DEGRADED_THRESHOLD_MS = 90_000;

class ConnectionHealthMonitor {
  private channels: Map<ChannelName, ChannelState> = new Map();
  private listeners: Set<HealthListener> = new Set();
  private checkTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    for (const ch of ['dm', 'collab'] as ChannelName[]) {
      this.channels.set(ch, {
        status: 'disconnected',
        phase: 'disconnected',
        lastPongAt: 0,
        lastConnectedAt: 0,
        reconnectCount: 0,
        latencyMs: 0,
        consecutiveFailures: 0,
        lastReconnectAt: 0,
        messagesReceived: 0,
        messagesSent: 0,
      });
    }
  }

  subscribe(listener: HealthListener): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  markConnected(channel: ChannelName): void {
    const state = this.channels.get(channel)!;
    const wasDisconnected = state.phase !== 'connected';
    state.status = 'healthy';
    state.phase = 'connected';
    state.lastConnectedAt = Date.now();
    state.lastPongAt = Date.now();
    state.consecutiveFailures = 0;
    if (wasDisconnected && state.lastConnectedAt > 0) {
      state.reconnectCount++;
    }
    this.notify(channel);
    this.ensurePeriodicCheck();
  }

  markReconnecting(channel: ChannelName): void {
    const state = this.channels.get(channel)!;
    state.phase = 'reconnecting';
    state.lastReconnectAt = Date.now();
    state.consecutiveFailures++;
    this.notify(channel);
  }

  markDisconnected(channel: ChannelName): void {
    const state = this.channels.get(channel)!;
    state.status = 'disconnected';
    state.phase = 'disconnected';
    this.notify(channel);
  }

  recordPong(channel: ChannelName, pingAt?: number): void {
    const state = this.channels.get(channel)!;
    state.lastPongAt = Date.now();
    if (pingAt) {
      state.latencyMs = Date.now() - pingAt;
    }
    if (state.status !== 'healthy') {
      state.status = 'healthy';
      this.notify(channel);
    }
  }

  recordMessageReceived(channel: ChannelName): void {
    this.channels.get(channel)!.messagesReceived++;
  }

  recordMessageSent(channel: ChannelName): void {
    this.channels.get(channel)!.messagesSent++;
  }

  getStatus(channel: ChannelName): HealthStatus {
    return this.channels.get(channel)!.status;
  }

  getPhase(channel: ChannelName): ConnectionPhase {
    return this.channels.get(channel)!.phase;
  }

  getState(channel: ChannelName): Readonly<ChannelState> {
    return { ...this.channels.get(channel)! };
  }

  getOverallStatus(): HealthStatus {
    const statuses = [...this.channels.values()].map((s) => s.status);
    if (statuses.every((s) => s === 'healthy')) return 'healthy';
    if (statuses.some((s) => s === 'disconnected')) return 'disconnected';
    return 'degraded';
  }

  getOverallPhase(): ConnectionPhase {
    const phases = [...this.channels.values()].map((s) => s.phase);
    if (phases.every((p) => p === 'connected')) return 'connected';
    if (phases.some((p) => p === 'disconnected')) return 'disconnected';
    return 'reconnecting';
  }

  getMetricsSnapshot(): Record<ChannelName, ChannelState> {
    const result: Record<string, ChannelState> = {};
    for (const [ch, state] of this.channels) {
      result[ch] = { ...state };
    }
    return result as Record<ChannelName, ChannelState>;
  }

  private ensurePeriodicCheck(): void {
    if (this.checkTimer) return;
    this.checkTimer = setInterval(() => {
      const now = Date.now();
      let anyActive = false;
      for (const [ch, state] of this.channels) {
        if (state.status === 'disconnected') continue;
        anyActive = true;
        const elapsed = now - state.lastPongAt;
        let newStatus: HealthStatus;
        if (elapsed < HEALTHY_THRESHOLD_MS) newStatus = 'healthy';
        else if (elapsed < DEGRADED_THRESHOLD_MS) newStatus = 'degraded';
        else newStatus = 'disconnected';
        if (newStatus !== state.status) {
          state.status = newStatus;
          this.notify(ch);
        }
      }
      if (!anyActive && this.checkTimer) {
        clearInterval(this.checkTimer);
        this.checkTimer = null;
      }
    }, 15_000);
  }

  private notify(channel: ChannelName): void {
    const state = this.channels.get(channel)!;
    for (const listener of this.listeners) {
      try { listener(channel, { ...state }); } catch { /* noop */ }
    }
  }
}

export const connectionHealth = new ConnectionHealthMonitor();
